/**
 * Croatian overrides for the admin screens.
 *
 * Only what genuinely differs from Bosnian. Croatian is ijekavica too, so
 * nothing moves for the jat reflex — what changes is vocabulary: *sustav*,
 * *tvrtka*, *datoteka*, *sigurnost*, *tipka*, and borrowed verbs in -irati.
 *
 * An entry identical to its Bosnian base is a bug, not a no-op: i18n.test.ts
 * fails on one, because a line that changes nothing is a decision that was
 * reverted in bs.ts without anybody revisiting this file.
 *
 * The admin screens turn out to be where these two standards diverge most,
 * and along four lines rather than one:
 *
 * - **Vocabulary.** *račun* not *nalog*, *tvrtka* not *firma*, *povijest*
 *   not *istorija*, *sustav* not *sistem*, *kriterij* not *kriterijum*,
 *   *znamenka* not *broj*, *popis* not *lista*, *šifrarnik* not *šifarnik*,
 *   *stroj* not *mašina*, *obvezno* not *obavezno*.
 * - **Printing.** Croatian *ispisati* against Bosnian *štampati*, which
 *   reaches every button and hint on the two badge screens.
 * - **Syntax.** Croatian avoids *da* plus a present tense: *prestaje
 *   važiti*, not *prestaje da važi*. And the future is written apart —
 *   *bit će*, *morat ćete*, *dobit ćete*.
 * - **The question words** in the log filters: *što* and *tko*.
 */

import type { AdminSlice } from '../bs/admin'

export const admin: Partial<AdminSlice> = {
  // ---- Shell -------------------------------------------------------
  'admin.subtitle': 'Korisnici, šifrarnici, 5S obrasci i dnevnik izmjena',

  // ---- Shared verbs ------------------------------------------------
  'admin.action.save': 'Spremi',
  'admin.saved': 'Izmjene su spremljene.',

  // ---- Users -------------------------------------------------------
  // *zaposlenik*, *račun*, *aktivacijski*, *priopćiti*, and the future written
  // apart — the same four moves as `form.noPassword` below, which says half of
  // this sentence inside the dialog.
  'admin.users.help':
    'Šifra zaposlenika je ono čime se korisnik prijavljuje i ono što piše na njegovoj kartici — po njoj ga aplikacija prepoznaje. Novi račun stiže neupotrebljiv: uz njega dobivate jednokratni aktivacijski kod koji priopćavate korisniku, a lozinku bira on sam i zna je samo on.',
  'admin.users.column.actions': 'Radnje',
  'admin.users.action.badge.tip.reissue': 'Kartica je izdana — izdaj novu',
  'admin.users.action.reset.tip': 'Izdaj novi aktivacijski kod',
  'admin.users.action.delete.tip.self': 'Ne možete obrisati vlastiti račun',
  'admin.users.action.delete.tip': 'Obriši račun — prijave ostaju, ali bez imena podnositelja',

  'admin.users.delete.confirm':
    'Obrisati korisnika „{name}"?\n\nNjegove prijave, ocjene i evaluacije ostaju — one su povijest pogona — ali više ne pokazuju tko ih je podnio. Ako to mora biti vidljivo, umjesto brisanja isključite „Račun je aktivan": pristup gubi jednako brzo, a ime ostaje.',

  'admin.users.form.noPassword':
    'Lozinku ne postavljate vi. Nakon spremanja dobit ćete aktivacijski kod koji priopćavate radniku — on sam bira lozinku pri prvoj prijavi.',

  'admin.users.field.code': 'Šifra zaposlenika',
  // *ispisana* rather than *odštampana*, the same move the two badge screens
  // make throughout.
  'admin.users.field.code.explain':
    'Mora biti jedinstvena — dvije je osobe ne mogu dijeliti; ako je kasnije promijenite, korisnik se od tada prijavljuje novom šifrom, a ona ispisana na njegovoj kartici više ne odgovara.',
  // *sinkronizacija*, as in `admin.synced.lastSyncedAt`.
  'admin.users.role.explain':
    'Uloge koje pregledaju tuđe prijave, ocjenjuju prijedloge i dodjeljuju nagrade postoje samo u KaProLean-u — KaproMES ne zna tko odobrava poboljšanje, pa se te uloge dodjeljuju ovdje i sinkronizacija ih ne dira.',
  'admin.users.field.email.hint': 'Nije obvezna.',
  'admin.users.field.notify': 'Šalji obavijesti i na e-poštu',
  'admin.users.field.notify.hint':
    'Obavijesti u aplikaciji stižu uvijek. Ovo vrijedi samo ako korisnik ima e-poštu.',
  'admin.users.field.active': 'Račun je aktivan',
  // *popis* not *spisak*.
  'admin.users.field.active.hint':
    'Neaktivan se korisnik ne može prijaviti i sve njegove otvorene sesije prestaju odmah, ali ostaje na popisu i njegove prijave i dalje nose njegovo ime.',

  'admin.users.activation.intro':
    'Priopćite radniku ovaj aktivacijski kod. Prikazuje se samo sada — ako se izgubi, izdajte novi.',

  'admin.users.reset.issue': 'Izdaj aktivacijski kod',
  // *račun* not *nalog*.
  'admin.users.reset.explain':
    'Kod vrijedi za jednu prijavu i račun do tada ne može ništa drugo — korisnik se njime prijavi i odmah postavi svoju lozinku.',
  'admin.users.reset.note':
    'Ne birate lozinku umjesto njih — dobivate kod za jednokratnu prijavu, a lozinku postavlja radnik.',
  'admin.users.reset.warning': 'Sve aktivne prijave tog korisnika bit će prekinute.',
  'admin.users.reset.codeIntro': 'Priopćite radniku ovaj kod. Prikazuje se samo sada.',

  'admin.users.badge.print': 'Ispiši karticu',
  'admin.users.badge.why':
    'Radnik skenira karticu umjesto tipkanja šifre i lozinke — brže kad se stoji pored stroja, s rukavicama.',
  'admin.users.badge.printHint':
    'Ispišite i predajte radniku. Skeniranjem kartice telefonom otvara se aplikacija i prijava je gotova.',
  // Same warning, same force. *Nosilac pristupa* and the photograph both
  // stay — softening either would leave a Croatian administrator with a
  // gentler rule than the one the token actually follows.
  'admin.users.badge.managerWarning':
    'Ovo nije radnički račun. Kartica je nosilac pristupa — fotografija kartice je dovoljna za prijavu. Za rukovoditelje i administratore uključite i dvofaktorsku zaštitu.',
  'admin.users.badge.alreadyIssued': 'Kartica je već izdana.',
  'admin.users.badge.alreadyIssuedAt': 'Kartica je već izdana — {when}.',
  // *točno*, *važeća* -> *valjana*, and *izdavanje nove* keeps the immediacy
  // the Bosnian has: the card dies at once, not eventually.
  'admin.users.badge.explain':
    'Svaki korisnik ima točno jednu valjanu karticu, pa izdavanje nove poništava onu koju nosi u džepu, odmah — tako se i opoziva izgubljena kartica.',

  // ---- Work centres ------------------------------------------------
  // `delete.confirm` is no entry any more: once the stale second paragraph came
  // out of the Bosnian, the question that was left is the same sentence in both
  // standards.
  'admin.workCenters.created': 'Radni centar je kreiran. Ispišite QR naljepnicu.',
  'admin.workCenters.noTemplates':
    'Novi radni centar ne može se kreirati dok ne postoji aktivan 5S obrazac za njegov tip — tip se bira s popisa obrazaca, a nijedan još nije napravljen. Otvorite „5S obrasci" i napravite prvi.',
  'admin.workCenters.field.code.hint':
    'Velika slova, znamenke, crtica. Koristi se u URL-ovima i na naljepnici.',
  'admin.workCenters.field.type.hint':
    'Radni centar ocjenjuje se po obrascu koji ovdje odaberete. Ako obrasca nema na popisu, prvo ga napravite u „5S obrasci".',
  'admin.workCenters.field.type.explain':
    'Isti obrazac može koristiti više radnih centara, a izmjena obrasca vrijedi za sve njih. Ako ovom mjestu treba vlastiti popis kriterija, napravite mu novi obrazac u „5S obrasci" i odaberite ga ovdje.',
  'admin.workCenters.field.type.placeholder': 'Odaberite 5S obrazac',
  'admin.workCenters.field.type.noMatch':
    'Nema obrasca s tim nazivom ili tipom. Novi obrazac izrađuje se u „5S obrasci".',
  'admin.workCenters.noQr.explain':
    'Stroj još nema QR kod, pa ga radnik ne može skenirati — može ga samo odabrati s popisa. Kod se izrađuje gumbom „Obnovi" u „QR naljepnice", a tamo se i ispisuje.',
  'admin.templates.addCentre.hint':
    'Otvara unos novog radnog centra s ovim obrascem već odabranim. Postojeći radni centar prebacujete na ovaj obrazac tako da ga uredite u „Radni centri" — osim radnih centara preuzetih iz KaproMES-a, čiji se tip mijenja u KaproMES-u.',
  'admin.workCenters.field.description.placeholder':
    'Neobvezno — čemu centar služi, gdje se nalazi u pogonu.',

  // ---- Plants ------------------------------------------------------
  'admin.plants.subtitle': 'Lokacije tvrtke. Svaki radni centar pripada točno jednom pogonu.',
  'admin.plants.help':
    'Hijerarhija je tvrtka → pogon → radni centar: svaka prijava i svaka 5S evaluacija visi o radnom centru, a svaki radni centar o točno jednom pogonu. Zato pogon postoji prije radnih centara koje drži. Brisanje pogona nije uklanjanje jedne lokacije — s njim trajno odlaze i svi radni centri u njemu, zajedno sa svime što je na njima prijavljeno i ocijenjeno.',
  'admin.plants.form.description': 'Lokacija tvrtke na kojoj se nalaze radni centri.',
  'admin.plants.field.code.hint':
    'Velika slova i znamenke. Pojavljuje se u izvješćima i na naljepnicama.',

  // ---- 5S templates ------------------------------------------------
  'admin.templates.help':
    'Obrasci su vaši — nova radna mjesta i nove kriterije unosite sami, bez nas. Redoslijed nije proizvoljan: prvo obrazac (naziv, tip radnog centra i skala), zatim pet kategorija, pa kriteriji u njima, i tek onda radni centar tog tipa. Radni centar ne može se spremiti dok njegov tip nema aktivan obrazac.',
  'admin.templates.meta':
    'tip: {type} · skala 1–{scale} · {items} kriterija · {evaluations} evaluacija',
  'admin.templates.stats': '{items} kriterija · {evaluations} evaluacija',
  'admin.templates.lockedNote':
    'Obrazac je korišten za predane evaluacije, pa se tekst kriterija i skala više ne mogu mijenjati — izmjena bi retroaktivno promijenila značenje ranijih ocjena. Da biste ga mijenjali, napravite novu verziju.',

  'admin.templates.locked.noAdd':
    'Kategorije i kriteriji ne mogu se dodavati jer su ranije evaluacije ocijenjene po ovom obrascu. Kategoriju po kojoj nitko nikada nije ocjenjivan možete obrisati — nema ocjene koju bi to promijenilo. Sve ostalo ide kroz „Nova verzija" na vrhu kartice.',

  'admin.templates.criterion': 'Kriterij',
  'admin.templates.criterion.delete.confirm': 'Ukloniti kriterij „{criterion}"?',
  'admin.templates.criterion.remove.hint':
    'Kriterij po kojem je već ocjenjivano ne briše se nego se deaktivira — ostaje ovdje precrtan kako bi ranije evaluacije i dalje bile čitljive.',
  'admin.templates.criterion.editTitle': 'Izmjena kriterija',
  'admin.templates.criterion.newTitle': 'Novi kriterij',
  'admin.templates.criterion.hint':
    'Tekst koji ocjenjivač čita na terenu — treba odgovarati tiskanom obrascu.',
  'admin.templates.criterion.saved': 'Spremljeno.',

  'admin.templates.guidance': 'Uputa',
  'admin.templates.guidance.hint':
    'Neobvezna pomoć ocjenjivaču. Može se mijenjati i na zaključanom obrascu.',
  'admin.templates.guidance.placeholder': 'npr. Provjeriti i ispod stola i iza stroja.',

  'admin.templates.category.added': 'Kategorija je dodana.',
  'admin.templates.category.delete.warning':
    'Briše se zajedno s kriterijima u njoj ({count}). Ako je po nekom od njih već ocjenjivano, brisanje se odbija — te ocjene bi ostale bez kriterija koji objašnjava što znače.',

  /*
   * A checklist written for one machine.
   *
   * Croatian and Bosnian agree on every sentence here except two words, and
   * both recur: **stroj** for a machine, and **kriterij** without the -um. The
   * overrides are exactly the strings one of those falls in — `machine.detach`,
   * `machine.field` and the two confirmations say the same thing in both
   * standards and are absent on purpose.
   */
  'admin.templates.settings.saved': 'Obrazac je spremljen.',
  'admin.templates.meta.machine':
    'obrazac za jedan stroj · skala 1–{scale} · {items} kriterija · {evaluations} evaluacija',
  'admin.templates.machine.badge': 'Jedan stroj',
  'admin.templates.machine.carveOut': 'Poseban obrazac za stroj',
  'admin.templates.machine.carveOut.hint':
    'Radi kopiju ovog obrasca samo za jedan stroj — original i ostali strojevi istog tipa ostaju nepromijenjeni, a kopiju odmah možete urediti.',
  'admin.templates.machine.carveOut.none':
    'Nijedan aktivan radni centar ne koristi ovaj tip, pa nema stroja koji bi se izdvojio.',
  'admin.templates.machine.carveOut.note':
    'Stroj se od sada ocjenjuje po svojoj kopiji, a ne po obrascu tipa. Ostali strojevi tog tipa se ne mijenjaju. Kad god poželite, možete ga vratiti na obrazac tipa.',
  'admin.templates.machine.field.hint':
    'Ponuđeni su strojevi koje ovaj obrazac sada pokriva — izdvajate jedan od njih.',
  'admin.templates.machine.detach.hint':
    'Stroj se ponovno ocjenjuje po obrascu svog tipa; ako je po ovoj kopiji već ocjenjivano, ona se arhivira umjesto da se obriše, da ranije evaluacije ostanu čitljive.',
  'admin.templates.machine.detach.warning':
    'Kriteriji napisani samo za ovaj stroj više se neće koristiti. Ako tip tog stroja nema aktivan obrazac, stroj se do daljnjega neće moći evaluirati — dobit ćete upozorenje ako je tako.',

  'admin.templates.field.type.option.gap.one': 'bez obrasca · {count} stroj',
  'admin.templates.field.type.option.gap.few': 'bez obrasca · {count} stroja',
  'admin.templates.field.type.option.gap.many': 'bez obrasca · {count} strojeva',
  // *postotak*, not *procenat*.
  'admin.templates.field.scale.locked':
    'Skala se ne može mijenjati jer su po ovom obrascu već predane evaluacije — svaki izračunati postotak računat je po njoj. Napravite novu verziju tipkom „Nova verzija" na vrhu kartice i u njoj upišite novu skalu.',

  // ---- Badge sheet -------------------------------------------------
  'admin.badgeSheet.title': 'Kartice za ispis',
  'admin.badgeSheet.print': 'Ispiši ({count})',
  'admin.badgeSheet.intro':
    'Odaberite radnike. Svatko dobiva svoju karticu koju skenira umjesto tipkanja šifre i lozinke.',
  'admin.badgeSheet.printHint':
    'Ispišite na A4, izrežite po isprekidanoj crti i plastificirajte. Kartice se prikazuju samo sada — kad zatvorite prozor, nema ih više.',
  'admin.badgeSheet.empty.description':
    'Kartice se izdaju samo radnicima, a nijedan još nije dodan.',
  'admin.badgeSheet.truncated':
    'Prikazano je prvih {shown} od {total}. Ostale izdajte iz popisa korisnika.',
  'admin.badgeSheet.reissue.one': 'Jedan odabrani radnik već ima karticu — stara prestaje važiti.',
  'admin.badgeSheet.reissue.many':
    '{count} odabranih radnika već imaju kartice — stare prestaju važiti.',
  'admin.badgeSheet.reissue.confirm.one':
    '{count} radnik već ima karticu.\n\nIzdavanjem nove, kartica koju sada nosi prestaje važiti odmah. Morat ćete mu predati novu.',
  'admin.badgeSheet.reissue.confirm.many':
    '{count} radnika već imaju karticu.\n\nIzdavanjem nove, kartice koje sada nose prestaju važiti odmah. Morat ćete im predati nove.',
  'admin.badgeSheet.partial': '{message} Izdano: {done} od {total}.',

  // ---- Activation codes for a whole shift --------------------------
  // *aktivacijski*, *račun*, *ispisati* and *izrezati po crti*, exactly as the
  // badge sheet above. `issue`, `truncated` and both empty-state lines read the
  // same in both standards and are inherited.
  'admin.activation.action': 'Aktivacijski kodovi',
  'admin.activation.title': 'Aktivacijski kodovi za ispis',
  // *Svatko dobiva* is the only thing that moves here; the rest of the sentence
  // is identical to the base.
  'admin.activation.intro':
    'Ovi korisnici još nemaju svoju lozinku. Svatko dobiva jednokratni kod kojim se prijavljuje i odmah postavlja lozinku koju zna samo on.',
  'admin.activation.print': 'Ispiši ({count})',
  'admin.activation.printHint':
    'Ispišite na A4, izrežite po isprekidanoj crti i predajte svakome njegov listić. Kodovi se prikazuju samo sada — kad napustite ovaj zaslon, nema ih više.',
  'admin.activation.empty.description':
    'Nijedan aktivan račun ne čeka aktivacijski kod. Kad netko izgubi lozinku, kod mu izdajete pojedinačno iz popisa korisnika.',
  'admin.activation.reissue':
    'Ako je nekome već izdan kod koji još nije iskoristio, taj kod prestaje važiti odmah — predajte mu novi listić.',
  // *šifrirani*, not *šifrovani*.
  'admin.activation.warning':
    'Kodovi se čuvaju šifrirani, pa se poslije ovoga ne mogu nigdje pročitati. Ispišite ih ili prepišite prije nego napustite zaslon; ako se izgube, izdajte nove.',
  'admin.activation.slip.employeeCode': 'Šifra zaposlenika',
  'admin.activation.slip.code': 'Aktivacijski kod',
  'admin.activation.slip.instructions':
    'Prijavite se šifrom zaposlenika i ovim kodom, a zatim postavite svoju lozinku — kod poslije toga više ne vrijedi.',
  // *sinkronizacija*, *računa*, *aktivacijski*.
  'admin.mes.sync.activations':
    'Sinkronizacija je otvorila {count} novih računa i svakom izdala aktivacijski kod.',

  // ---- QR stickers on the machines ---------------------------------
  // *stroj*, *ispisati*, *nikamo*, *optjecaj*.
  'admin.stickers.help':
    'Obnavljanje koda poništava svaku naljepnicu koja je već na tom stroju: stari kod od tog trenutka ne vodi nikamo, pa radnik koji ga skenira dobiva poruku da kod nije poznat i mora sam birati radni centar. Obnovite kod tek kad je naljepnica oštećena ili kad stari kod ne smije više biti u optjecaju, i odmah ispišite i zalijepite novu.',

  // ---- Activity log ------------------------------------------------
  // *tko* and *što*, as in the two filter labels below, plus *izdan* for the
  // participles.
  'admin.log.help':
    'Dnevnik sam bilježi izmjene na prijavama i evaluacijama, polje po polje, sa starom i novom vrijednošću; uz to administratorske stranice upisuju i ono što nije izmjena zapisa — izdan aktivacijski kod, izdana ili poništena kartica, nova verzija obrasca, obnovljen QR kod, promijenjena valuta. Čitanje se ne bilježi: dnevnik odgovara na pitanje tko je što promijenio, a ne tko je što vidio.',
  'admin.log.filter.subject': 'Što je mijenjano',
  'admin.log.filter.causer': 'Tko je mijenjao',
  'admin.log.empty.description':
    'Dnevnik se puni kada korisnici mijenjaju prijave, evaluacije i šifrarnike.',
  'admin.log.causer.system': 'Sustav',

  // ---- MES integration ---------------------------------------------
  // *sinkronizacija*, *stroj*, *filtar*, and the reflexive after the verb —
  // *deaktivira se*, *sinkronizira se*.
  'admin.mes.help':
    'Podaci idu samo u jednom smjeru: KaproMES je vlasnik pogona, radnih centara i ljudi, pa se polja koja on drži ovdje ne mogu mijenjati — izmjena bi ostala do noćne sinkronizacije i onda nestala. Sinkronizacija ništa ne briše: radni centar ili osoba koja se prestane pojavljivati u odgovoru deaktivira se, jer je izostanak iz odgovora mnogo češće greška u vezi ili filtar nego stroj koji je zaista otpisan. Preuzeta osoba ne može se prijaviti dok sama ne postavi lozinku — lozinke se ne preuzimaju i ne tražimo ih.',
  'admin.mes.roles.explain':
    'Preko sinkronizacije prelazi samo administratorska uloga i samo naviše: tko je administrator u KaproMES-u postaje administrator i ovdje pri sljedećem preuzimanju, a gubitak te uloge tamo je ne oduzima ovdje — uloge koje odlučuju o prijedlozima i nagradama KaproMES ne poznaje, pa bi ih spuštanje svake noći brisalo.',
  'admin.mes.report.explain':
    'Izvješće nakon preuzimanja nabraja i podignute administratore i uloge iz KaproMES-a koje ne prepoznajemo — takav se račun i dalje sinkronizira, samo mu se uloga ne dira.',

  // Rows the MES sync owns. See SyncedFromBadge for why the reason is
  // spelled out rather than the field merely being greyed.
  'admin.synced.lastSyncedAt': 'Posljednja sinkronizacija: {at}',
  'admin.synced.fieldLocked': 'Održava {system} — sljedeća sinkronizacija vratila bi izmjenu.',

  // The defect list above the templates — see pages/admin/TemplateGaps.tsx.
  // *stroj* not *mašina*, *što* not *šta*, and the infinitive rather than *da*
  // plus a present tense.
  'admin.templates.gaps.help':
    'Radnik koji stane pred ove strojeve ne može obaviti 5S obilazak: njihov tip nema aktivan obrazac, pa aplikacija nema što prikazati. Označite tipove, odaberite postojeći obrazac koji im najviše odgovara i primijenite ga — ili napravite novi obrazac za svaki tip.',
  'admin.templates.gaps.apply.hint':
    'Kopira sve kategorije i kriterije odabranog obrasca na svaki označeni tip; tip koji već ima aktivan obrazac preskače se, a ne prepisuje.',

  // *stupanj* not *stepen*, *postotak* not *procenat*, *predan* not *predat*.
  'admin.templates.field.scale.hint': 'Obično 5 — toliko stupnjeva ocjene ima svaki kriterij.',
  'admin.templates.field.scale.explain':
    'Skala 1–5 daje ocjenjivaču pet stupnjeva i finiju sliku stanja, a 1–3 se brže popunjava na terenu; broj se zaključava nakon prve predane evaluacije, jer je svaki već izračunat postotak izračunat po njemu.',

  // ---- Company settings --------------------------------------------
  // *tvrtka*, *izvješće*, *tablica*, *obavijesti*, *tečaj* not *kurs*.
  'admin.settings.help':
    'Valuta je jedna za cijelu tvrtku: mijenja oznaku uz svaki iznos — na ekranu, u PDF izvješću, u tablici i u obavijestima. Brojevi se pri tome ne preračunavaju: 500 ostaje 500 i samo dobiva novu oznaku, jer aplikacija nema tečaj, a izmišljen tečaj bio bi gori od promjene oznake.',

  // *definirati*, *usporediti*, *izmijeniti* -> *urediti*, *obvezan*.
  'admin.shifts.edit': 'Uredi',
  'admin.shifts.empty.title': 'Nema definiranih smjena',
  'admin.shifts.updated': 'Smjena je uređena.',
  'admin.shifts.help':
    'Smjene su neobvezne: ako ih ne unesete, polje za smjenu uopće se ne prikazuje na 5S obrascu. Unesite ih ako želite usporediti kako se radni centri održavaju iz smjene u smjenu — evaluator tada bira smjenu pri svakom obilasku. Noćna smjena završava prije nego što počne i tako se i upisuje.',
  'admin.shifts.form.description': 'Naziv je obvezan; vrijeme nije.',
  'admin.shifts.field.window.explain':
    'Vrijeme služi samo kako bi obrazac sam ponudio smjenu koja traje u trenutku obilaska; evaluator tu ponudu mijenja jednim dodirom, a smjena bez vremena bira se ručno.',

  // ---- Tooltips across Administracija ----
  // The same four moves as the rest of this file: *račun*, *aktivacijski*,
  // *sinkronizacija*, *stroj*, *popis*, *tko*; *ispisati* and *tipka* for
  // *štampati* and *dugme*; *spremiti* for *sačuvati*. The plant's work-centre
  // count and the currency count read the same in both and are inherited.
  'admin.users.toolbar.explain':
    '„Aktivacijski kodovi" izdaje i ispisuje jednokratne kodove za sve koji još nemaju svoju lozinku; „Kartice" izdaje i ispisuje QR kartice kojima se radnici prijavljuju skeniranjem.',
  'admin.users.column.role.explain':
    'Kod deaktiviranog računa ovdje piše „Neaktivan" umjesto uloge. Uloga ostaje spremljena i vraća se čim račun ponovno aktivirate.',
  'admin.users.column.lastLogin.explain':
    'Upisuje se pri svakoj prijavi — lozinkom, aktivacijskim kodom ili karticom. „Nikad" znači da se na račun još nitko nije prijavio; obično osoba još nije dobila ili iskoristila aktivacijski kod.',
  'admin.users.field.plant.explain':
    'Pogon je samo oznaka gdje je osoba zavedena — vidi se na njezinu profilu i u broju korisnika na „Pogoni", ali ne određuje na kojim radnim centrima radi. Sinkronizacija iz KaproMES-a upisuje ga samo kad je prazan.',
  'admin.users.field.email.explain':
    'Obavijesti stižu na nju samo ako je uključeno „Šalji obavijesti i na e-poštu". Zaboravljenu lozinku korisnik može sam vratiti putem e-pošte tek kad adresu potvrdi na svom profilu.',
  'admin.users.field.active.synced':
    'Ovaj račun održava KaproMES i svaka mu sinkronizacija postavlja aktivnost prema statusu u KaproMES-u — ako je osoba ondje i dalje aktivna, deaktivacija ovdje traje samo do sljedeće sinkronizacije.',
  'admin.users.delete.codeKept':
    'Šifra zaposlenika {code} ostaje zauzeta i nakon brisanja — novi je račun ne može dobiti.',
  'admin.users.reset.badgeStays':
    'Novi kod ne dira QR karticu: ona i dalje prijavljuje ovog korisnika. Ako je kartica izgubljena ili fotografirana, poništite i nju tipkom s karticom u njegovu retku.',
  'admin.users.badge.revoke.explain':
    '„Poništi" odmah gasi karticu i ne izdaje novu — radnik se dalje prijavljuje šifrom i lozinkom. Za zamjenu izgubljene kartice upotrijebite „Izdaj novu karticu".',
  'admin.users.badge.closeUnprinted':
    'Zatvorite li prozor prije ispisa, kartica ostaje izdana, ali se više ne može prikazati — tada izdajte novu.',

  'admin.plants.field.name.explain':
    'Naziv pogona ispisuje se na QR naljepnicama njegovih radnih centara. Naljepnice koje su već zalijepljene rade i nakon promjene, samo nose stari naziv.',
  'admin.plants.userCount.explain':
    'Korisnici kojima je ovaj pogon upisan na „Korisnici", i aktivni i deaktivirani. Brisanjem pogona računi ostaju, samo bez pogona.',

  'admin.shifts.subtitle.explain':
    'S definiranim smjenama isti radni centar ima jednu 5S evaluaciju dnevno za svaku smjenu; bez smjena — jednu dnevno.',
  'admin.shifts.field.name.explain':
    'Ovaj naziv evaluator bira na 5S obrascu i njime su označeni grafikoni po smjenama. Preimenovanje se vidi i na ranijim evaluacijama — naziv se ne pamti zasebno uz svaku.',
  'admin.shifts.field.endsAt.explain':
    'Upišite oba vremena ili nijedno — samo početak ili samo kraj ne može se spremiti. Isti početak i kraj ne pokrivaju nijedan trenutak, pa takvu smjenu obrazac nikad ne ponudi sam.',
  'admin.shifts.field.sortOrder.explain':
    'Tim se redom smjene nude na 5S obrascu i slažu na grafikonima. Ako se vremena dviju smjena preklapaju, obrazac sam ponudi onu koja je ovdje prva.',

  'admin.settings.currency.undo.explain':
    'Zato se promjena može i vratiti: odaberite staru valutu i iznosi opet dobiju staru oznaku. Isto se tada događa i iznosima upisanima u međuvremenu, iako su upisani u novoj.',

  'admin.mes.active.explain':
    'Sinkronizacija postavlja i aktivnost. Radnik je aktivan dok je aktivan u KaproMES-u — deaktivacija ovdje traje samo do sljedeće sinkronizacije. Radni centar iz KaproMES-a ovdje se ne može ni deaktivirati: aktivan je dok ga KaproMES vraća.',
  'admin.mes.save.explain':
    '„Testiraj vezu" i „Sinkroniziraj sada" rade sa spremljenim podacima. Nakon izmjene adrese ili tokena prvo pritisnite „Spremi", pa tek onda testirajte.',

  'admin.templates.gaps.body.explain':
    'Broje se samo aktivni radni centri. Stroj koji ima vlastiti obrazac ne broji se, iako njegov tip nema obrazac.',
  'admin.templates.gaps.source.explain':
    'Broj u zagradi je broj kriterija koji se kopiraju. Kopija dobiva istu skalu ocjena i naziv „5S — tip", koji poslije možete promijeniti.',

  'admin.log.search.explain':
    'Pretraga gleda samo tekst zapisa, a on je uvijek na bosanskom — npr. šifra zaposlenika ili „Obrisan". Izmjene prijava i evaluacija tako se ne pronalaze; za njih upotrijebite filtre ispod.',
  'admin.log.filter.event.explain':
    'Vrstu izmjene imaju samo automatski zapisi o prijavama i 5S evaluacijama. Čim je odaberete, nestaju zapisi administratorskih stranica — izdani kodovi, kartice, obrisani korisnici i slično.',
  'admin.log.filter.logName.explain':
    'Dio aplikacije koji je upisao zapis, pod internim nazivom: user su korisnici, work_center radni centri, five_s 5S evaluacije, submission prijave, mes sinkronizacija s KaproMES-om.',
  'admin.log.causer.system.explain':
    'Zapis bez korisnika iza sebe: upisao ga je automatski posao, npr. noćna sinkronizacija s KaproMES-om — ili je korisnik koji ga je napravio u međuvremenu obrisan.',
  'admin.log.impersonated.explain':
    'Izmjenu je napravila podrška KaProLean-a prijavljena kao ovaj korisnik: ime ispred je račun koji je korišten, a ovdje piše tko je zaista radio.',

  'admin.badgeSheet.intro.explain':
    'Na popisu su samo radnici — karticu za nekoga drugog izdajete pojedinačno s popisa korisnika. Unaprijed su označeni aktivni radnici koji još nemaju karticu.',
  'admin.badgeSheet.closeUnprinted':
    'Zatvorite li prozor prije ispisa, kartice ostaju izdane, ali se više ne mogu prikazati. Tada ih izdajte ponovno — ovdje ih označite ručno, jer ih popis više ne označava sam.',
  'admin.activation.intro.explain':
    'Na popisu su aktivni računi bez vlastite lozinke: novi, preuzeti iz KaproMES-a i oni kojima je pristup vraćen kodom. Tko je već postavio svoju lozinku, ne dobiva kod i lozinka mu ostaje.',
  'admin.activation.lostSlip':
    'Izgubljen listić otvara taj račun dok ga netko ne iskoristi — tom radniku odmah izdajte novi kod tipkom s ključem u njegovu retku. Ne pokrećite ovo izdavanje ponovno: poništilo bi listiće svih koji još čekaju.',

  'admin.twoFactorRequired.body':
    'Vaša tvrtka traži da svaki administrator ima uključenu dvofaktorsku zaštitu. Dok je ne uključite, administracija je zaključana — ostatak aplikacije radi kao i obično.',
  'admin.twoFactorRequired.how':
    'Trebate aplikaciju za autentifikaciju na mobitelu, npr. Google Authenticator ili Microsoft Authenticator. Postavljanje traje oko minutu.',
}
